
import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

function buildParams(filters = {}) {
  const params = {};
  Object.entries(filters).forEach(([k, v]) => {
    if (v !== '' && v !== null && v !== undefined) params[k] = v;
  });
  return params;
}

// ── KPI Summary ──────────────────────────────────────────────
export async function fetchKpiSummary(filters = {}) {
  const { data } = await api.get('/api/analytics/kpi', { params: buildParams(filters) });
  return data;
}

// ── Revenue by Month ─────────────────────────────────────────
export async function fetchRevenueByMonth(filters = {}) {
  const { data } = await api.get('/api/analytics/revenue-by-month', { params: buildParams(filters) });
  return data;
}

// ── Daily Revenue ────────────────────────────────────────────
export async function fetchDailyRevenue(filters = {}) {
  const { data } = await api.get('/api/analytics/daily-revenue', { params: buildParams(filters) });
  return data;
}

// ── Revenue by Warehouse ─────────────────────────────────────
export async function fetchRevenueByWarehouse(filters = {}) {
  const { data } = await api.get('/api/analytics/revenue-by-warehouse', { params: buildParams(filters) });
  return data;
}

// ── Revenue by Order Type ────────────────────────────────────
export async function fetchRevenueByType(filters = {}) {
  const { data } = await api.get('/api/analytics/revenue-by-type', { params: buildParams(filters) });
  return data;
}

// ── Orders by Status ─────────────────────────────────────────
export async function fetchOrdersByStatus(filters = {}) {
  const { data } = await api.get('/api/analytics/orders-by-status', { params: buildParams(filters) });
  return data;
}

// ── Payment Status Breakdown ─────────────────────────────────
export async function fetchPaymentStatus(filters = {}) {
  const { data } = await api.get('/api/analytics/payment-status', { params: buildParams(filters) });
  return data;
}

// ── Shipping Status Breakdown ────────────────────────────────
export async function fetchShippingStatus(filters = {}) {
  const { data } = await api.get('/api/analytics/shipping-status', { params: buildParams(filters) });
  return data;
}

// ── Quantity Status Breakdown ────────────────────────────────
export async function fetchQuantityStatus(filters = {}) {
  const { data } = await api.get('/api/analytics/quantity-status', { params: buildParams(filters) });
  return data;
}

// ── Top Customers ────────────────────────────────────────────
export async function fetchTopCustomers(filters = {}) {
  const { data } = await api.get('/api/analytics/top-customers', { params: buildParams(filters) });
  return data;
}

// ── Owner Performance ────────────────────────────────────────
export async function fetchOwnerPerformance(filters = {}) {
  const { data } = await api.get('/api/analytics/owner-performance', { params: buildParams(filters) });
  return data;
}

// ── Filter Options ───────────────────────────────────────────
export async function fetchFilterOptions() {
  const { data } = await api.get('/api/analytics/filter-options');
  return data;
}

// ── Recent Orders (paginated) ────────────────────────────────
export async function fetchRecentOrders(filters = {}, page = 1, limit = 15) {
  const { data } = await api.get('/api/analytics/recent-orders', {
    params: { ...buildParams(filters), page, limit },
  });
  return data;
}

// ── Customer Stats ───────────────────────────────────────────
export async function fetchCustomerStats(filters = {}) {
  const { data } = await api.get('/api/analytics/customer-stats', { params: buildParams(filters) });
  return data;
}

// ── Top Sellers by Year ──────────────────────────────────────
export async function fetchTopSellers(year, limit = 20, unitsPerCase = 12, mergeSku = true) {
  const { data } = await api.get('/api/analytics/top-sellers', {
    params: { year, limit, unitsPerCase, mergeSku },
  });
  return data;
}

// ── Regions by Country (cascading) ───────────────────────────
export async function fetchRegionsByCountry(country) {
  if (!country) return { country: null, regions: [] };
  const { data } = await api.get('/api/analytics/regions-by-country', {
    params: { country },
  });
  return data;
}

// ═══════════════════════════════════════════════════════════════
// 🆕 COMPARATIVE ENDPOINTS (3-year side-by-side with deltas)
// ═══════════════════════════════════════════════════════════════

// ── KPI Comparative (YTD vs SPLY vs 2YA) ────────────────────
export async function fetchKpiComparative(mode = 'ytd', filters = {}) {
  const { data } = await api.get('/api/analytics/kpi-comparative', {
    params: { mode, ...buildParams(filters) },
  });
  return data;
}

// ── Top Sellers Comparative (3 years with deltas + CS→units) ─
export async function fetchTopSellersComparative(year, limit = 20, unitsPerCase = 12, mergeSku = true) {
  const { data } = await api.get('/api/analytics/top-sellers-comparative', {
    params: { year, limit, unitsPerCase, mergeSku },
  });
  return data;
}

// ── Top Customers Comparative ────────────────────────────────
export async function fetchTopCustomersComparative(year, limit = 10) {
  const { data } = await api.get('/api/analytics/top-customers-comparative', {
    params: { year, limit },
  });
  return data;
}

// ── Owner Performance Comparative ────────────────────────────
export async function fetchOwnerPerformanceComparative(year) {
  const { data } = await api.get('/api/analytics/owner-performance-comparative', {
    params: { year },
  });
  return data;
}

// ── Warehouse Comparative ────────────────────────────────────
export async function fetchWarehouseComparative(year) {
  const { data } = await api.get('/api/analytics/revenue-by-warehouse-comparative', {
    params: { year },
  });
  return data;
}

// ── Revenue by Month Comparative ─────────────────────────────
export async function fetchRevenueByMonthComparative(mode = 'ytd', filters = {}) {
  const { data } = await api.get('/api/analytics/revenue-by-month-comparative', {
    params: { mode, ...buildParams(filters) },
  });
  return data;
}


// Admin Data Governance Layer: Family Management routes
export async function fetchCustomerFamilies(filters = {}) {
  const { data } = await api.get('/api/analytics/admin/families', { params: buildParams(filters) });
  return data;
}

// Admin Data Governance Layer: Family Management routes
export async function addCustomerFamily(filters = {}) {
  const { data } = await api.post('/api/analytics/admin/families', { params: buildParams(filters) });
  return data;
}